// 2.39 "Your strongest matches are running low" and 10.20 "Replies from a search fell": one prompt per search, raised by
// the engine (engine/search_health.py) as a task for the owner. Shown on Settings › Sending › Mailbox and search health (and replies on Results), in the approval queue and in
// the Home's "Waiting on you". Each suggestion (a country, a nearby niche, a filter dropped) opens a NEW search
// that waits for the owner's approval in Find prospects (2.19): nothing runs before that. Find › Results shows the
// reply rates per search (SearchRates).
import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Radar, TrendingDown } from "lucide-react";
import type { Crm } from "../../lib/crm";
import type { HumanTask } from "../../lib/types";
import { useCrm } from "../../lib/session";
import { useAsync } from "../../lib/useAsync";
import { fmtNumber } from "../../lib/format";
import { Async, Notice } from "../../components/ui";
import { Button, Card, FoldCard } from "../../components/kit";
import {
  changeOf,
  pctText,
  promptOf,
  promptSummary,
  SEARCH_LOW,
  SEARCH_PROMPT_KINDS,
  SEARCH_REPLIES,
  searchRates,
  suggestionDetail,
  suggestionLabel,
  type SearchHealth,
} from "../../lib/searchHealth";
import "./find-review.css";

/** Approvals.tsx TASKS_CHANGED (not imported: Approvals imports this file). Tells the menu its counts changed. */
const TASKS_CHANGED = "ogma:tasks-changed";

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** The prompt's words, its suggestions as buttons, and "Not now". Used in the fold card and in the approval queue. */
export function SearchPromptBody({ crm, task, onDone, primary = true }: { crm: Crm; task: HumanTask; onDone: () => void; primary?: boolean }) {
  const navigate = useNavigate();
  const p = promptOf(task);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const searchId = p.search_id ?? task.entity_id ?? null;

  const widen = async (i: number) => {
    const s = p.suggestions![i]!;
    if (!searchId) return;
    setBusy(`s${i}`);
    setError(null);
    try {
      const r = await crm.widenProspectSearch(searchId, changeOf(s));
      window.dispatchEvent(new Event(TASKS_CHANGED));
      onDone();
      navigate(`/contacts/find?search=${encodeURIComponent(r.search_id)}#find-prospects`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };
  const notNow = async () => {
    setBusy("later");
    setError(null);
    try {
      await crm.completeTask(task.id, "cancelled", { not_now: true });
      window.dispatchEvent(new Event(TASKS_CHANGED));
      onDone();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const suggestions = p.suggestions ?? [];
  return (
    <div className="search-prompt">
      <p>{p.message}</p>
      {task.kind === SEARCH_LOW && suggestions.length > 0 && (
        <>
          <p className="muted small">Widen it with one tap. Each opens a new search for you to check and approve; nothing runs before you do.</p>
          <ul className="search-suggestions" aria-label="Ways to widen this search">
            {suggestions.map((s, i) => (
              <li key={`${s.kind}-${String(s.value ?? i)}`}>
                <Button variant={primary && i === 0 ? "primary" : "secondary"} small icon={ArrowRight} disabled={busy !== null} onClick={() => void widen(i)}>
                  {busy === `s${i}` ? "Opening…" : suggestionLabel(s)}
                </Button>
                <span className="muted small"> {suggestionDetail(s, p.noun)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {task.kind === SEARCH_LOW && suggestions.length === 0 && (
        <p className="muted small">The prospect database has nothing nearby to add yet. Try a new search in Find prospects.</p>
      )}
      <div className="fold-actions">
        {task.kind === SEARCH_REPLIES && (
          <Button variant={primary ? "primary" : "secondary"} small to="/results/outreach#by-search">
            See the rates by search
          </Button>
        )}
        <Button variant="ghost" small disabled={busy !== null} onClick={() => void notNow()}>
          {busy === "later" ? "Saving…" : "Not now"}
        </Button>
      </div>
      <Notice text={error} tone="bad" />
    </div>
  );
}

/** The approval queue's card for the two kinds (KINDS in screens/approvals/Approvals.tsx). */
export function SearchPromptTask({ crm, task, onDone }: { crm: Crm; task: HumanTask; onDone: () => void }) {
  return <SearchPromptBody crm={crm} task={task} onDone={onDone} primary={false} />;
}

/**
 * Settings › Sending › Mailbox and search health (both kinds) and Results (replies dropping): the open prompts as fold cards in the
 * reminder tone, the first one open. `empty` shows when none wait; nothing shows by default.
 */
export function SearchPrompts({ kinds = SEARCH_PROMPT_KINDS, empty = null, nested = false, onCount }: { kinds?: string[]; empty?: ReactNode; nested?: boolean; onCount?: (n: number) => void } = {}) {
  const crm = useCrm();
  const [tick, setTick] = useState(0);
  const tasks = useAsync(() => crm.openHumanTasks().then((all) => all.filter((t) => kinds.includes(t.kind))), [crm, tick, kinds.join(",")]);
  const count = tasks.data ? tasks.data.length : null;
  useEffect(() => {
    if (count !== null) onCount?.(count);
  }, [count]);
  if (!tasks.data) return null;
  if (tasks.data.length === 0) return <>{empty}</>;
  return (
    <section className="block search-prompts" aria-label="Your searches">
      <div className="fold-stack">
        {tasks.data.map((t, i) => (
          <FoldCard
            key={t.id}
            id={`search-prompt-${t.id}`}
            persist={false}
            waiting
            variant={nested ? "row" : "card"}
            level={nested ? 3 : 2}
            icon={t.kind === SEARCH_REPLIES ? TrendingDown : Radar}
            tone="ochre"
            title={t.kind === SEARCH_REPLIES ? "Replies are dropping" : "Your strongest matches are running low"}
            summary={promptSummary(t)}
            defaultOpen={i === 0}
          >
            <SearchPromptBody crm={crm} task={t} onDone={() => setTick((n) => n + 1)} primary={i === 0} />
          </FoldCard>
        ))}
      </div>
    </section>
  );
}

/** Find › Results: reply and positive-reply rates per search, all batches and the last three against those before. */
export function SearchRatesView({ h }: { h: SearchHealth }) {
  const n = Number(h.settings.replies?.batches ?? 3) || 3;
  const days = Number(h.settings.replies?.mature_days ?? 14) || 14;
  const rows = h.searches.filter((s) => (s.replies?.batches ?? []).length > 0);
  return (
    <Card
      id="by-search"
      level={2}
      title="By search"
      subtitle={rows.length ? `${fmtNumber(rows.length)} ${rows.length === 1 ? "search" : "searches"} with replies to count` : "No messages old enough to count yet"}
    >
      {rows.length === 0 ? (
        <p className="muted">No first messages from your searches are old enough to count yet. A message counts {days} days after it goes.</p>
      ) : (
        <div className="table-wrap">
          <table className="stack">
            <thead>
              <tr>
                <th scope="col">Search</th>
                <th scope="col">First messages</th>
                <th scope="col">Reply rate</th>
                <th scope="col">Positive rate</th>
                <th scope="col">Last {n} batches</th>
                <th scope="col">Before</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => {
                const r = searchRates(s, n);
                return (
                  <tr key={s.search_id}>
                    <td data-label="Search">{s.title}</td>
                    <td data-label="First messages">{fmtNumber(r.all.sent)}</td>
                    <td data-label="Reply rate">{pctText(r.all.reply_rate)}</td>
                    <td data-label="Positive rate">{pctText(r.all.positive_rate)}</td>
                    <td data-label={`Last ${n} batches`}>
                      {pctText(r.recent.reply_rate)} <span className="muted small">({fmtNumber(r.recent.sent)} sent)</span>
                    </td>
                    <td data-label="Before">
                      {r.before.sent ? (
                        <>
                          {pctText(r.before.reply_rate)} <span className="muted small">({fmtNumber(r.before.sent)} sent)</span>
                        </>
                      ) : (
                        "–"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="muted small">
        A batch is a week of first messages, including those you sent by hand. Replies count within {days} days of the message, for every batch alike. Ogma tells you when a search's replies
        fall, and only when the drop is clear by the same rule as your A/B tests ({fmtNumber(Number(h.settings.replies?.min_sends ?? 100))} messages
        on each side).
      </p>
    </Card>
  );
}

export function SearchRates() {
  const crm = useCrm();
  const data = useAsync(() => crm.searchHealth().catch(() => null), [crm]);
  return (
    <Async state={data} label="Loading rates by search">
      {(h) => (h ? <SearchRatesView h={h} /> : null)}
    </Async>
  );
}
